import { useEffect, useRef, useState } from "react";
import api, { fileUrl, uploadFile, asItems } from "@/lib/api";
import { timeOnly, relTime, dt, CHANNEL_LABELS, CONV_STATE_LABELS, ENVIO_LABELS, stripMarkdown, contactAlias } from "@/lib/format";
import { Loader, EmptyState } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  MessageCircle, Send, Plus, Pause, Play, Sparkles, ImagePlus,
  BookmarkPlus, ArrowLeft, Bot, User, UserCog, Search, Zap,
  StickyNote, Trash2, Instagram, Phone, FileSignature, Globe, UserCheck,
} from "lucide-react";
import { toast } from "sonner";

const CHANNEL_ICON = { whatsapp: Phone, instagram: Instagram, simulador: MessageCircle, web: Globe };

function Bubble({ m, onMarkExample }) {
  const isClient = m.rol === "cliente";
  const isAgent = m.rol === "agente";
  const align = isClient ? "items-start" : "items-end";
  const bg = isClient ? "bg-[#F3F4F6] text-foreground" : isAgent ? "bg-[#FDF2F2] text-foreground" : "bg-[#FFF9E6] text-foreground";
  return (
    <div className={`flex flex-col ${align} bubble-in`}>
      <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${bg}`}>
        {m.tipo === "audio" && <p className="text-xs italic text-muted-foreground mb-1">🎤 Audio recibido</p>}
        {m.tipo === "imagen" && m.media_url && (
          <img src={fileUrl(m.media_url)} alt="" className="rounded-xl mb-1 max-h-56 object-cover" />
        )}
        {m.contenido && <p className="text-sm whitespace-pre-wrap">{stripMarkdown(m.contenido)}</p>}
      </div>
      <div className="flex items-center gap-2 mt-1 px-1">
        {m.rol === "humano" && m.autor_nombre && <span className="text-[10px] font-medium text-amber-700" data-testid={`autor-${m.id}`}>{m.autor_nombre}</span>}
        <span className="text-[10px] text-muted-foreground">{timeOnly(m.timestamp)}</span>
        {m.estado_envio && ENVIO_LABELS[m.estado_envio] && (
          <span className="text-[10px] text-muted-foreground">· {ENVIO_LABELS[m.estado_envio]}</span>
        )}
        {m.rol === "humano" && !m.marcada_como_ejemplo && (
          <button onClick={() => onMarkExample(m)} className="text-[10px] text-primary flex items-center gap-1" data-testid={`mark-example-${m.id}`}>
            <BookmarkPlus className="h-3 w-3" /> Marcar como ejemplo
          </button>
        )}
        {m.marcada_como_ejemplo && <span className="text-[10px] text-emerald-600 flex items-center gap-1"><BookmarkPlus className="h-3 w-3" /> Ejemplo guardado</span>}
      </div>
    </div>
  );
}

const stateBadge = (estado) => ({
  bot: "bg-[#FDF2F2] text-primary",
  humano: "bg-[#FFF9E6] text-amber-700",
  derivada: "bg-red-50 text-red-600",
}[estado] || "bg-gray-100");

const FILTROS = [
  { key: "todas", label: "Todas" },
  { key: "mias", label: "Mis conversaciones" },
  { key: "sin_asignar", label: "Sin asignar" },
  { key: "esperando_humano", label: "Esperando humano" },
];

function initials(name) {
  if (!name) return "?";
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

export default function Conversations() {
  const { user: me } = useAuth();
  const [searchParams] = useSearchParams();
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState(searchParams.get("vista") || "todas");
  const [listQuery, setListQuery] = useState("");
  const [active, setActive] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const [pendingImg, setPendingImg] = useState(null);
  const [msgQuery, setMsgQuery] = useState("");
  const [quickReplies, setQuickReplies] = useState([]);
  const [newQuickReply, setNewQuickReply] = useState("");
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplId, setTplId] = useState("");
  const [tplValores, setTplValores] = useState({});
  const scrollRef = useRef(null);

  const loadList = async () => {
    const params = filter !== "todas" ? { vista: filter } : {};
    const r = await api.get("/conversations", { params });
    setList(asItems(r));
  };
  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { api.post("/notifications/marcar-leidas", null, { params: { tipo: "derivacion" } }); }, []);
  useEffect(() => { api.get("/respuestas-rapidas").then((r) => setQuickReplies(r.data)); }, []);
  useEffect(() => { api.get("/plantillas").then((r) => setTemplates(r.data)); }, []);
  useEffect(() => { api.get("/usuarios-activos").then((r) => setActiveUsers(r.data)); }, []);

  const openConv = async (c) => {
    const r = await api.get(`/conversations/${c.id}`);
    setActive(r.data);
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 100);
  };

  const refreshActive = async () => {
    if (!active) return;
    const r = await api.get(`/conversations/${active.id}`);
    setActive(r.data);
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 100);
  };

  const newSimulator = async () => {
    const r = await api.post("/conversations/simulador");
    await loadList();
    openConv(r.data);
    toast.success("Conversación de prueba creada");
  };

  const isHuman = active && (active.estado === "humano" || active.agente_pausado);
  const canSendClient = active && active.canal === "simulador" && !isHuman;

  const pickImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { const { path } = await uploadFile(f); setPendingImg(path); toast.success("Imagen lista para enviar"); }
    catch { toast.error("No se pudo subir la imagen"); }
    e.target.value = "";
  };

  const sendAsClient = async () => {
    if (!text.trim() && !pendingImg) return;
    setSending(true);
    const payload = pendingImg
      ? { contenido: text, tipo: "imagen", media_url: pendingImg }
      : { contenido: text, tipo: "texto" };
    const optimistic = { id: `optimistic-${Date.now()}`, rol: "cliente", timestamp: new Date().toISOString(), ...payload };
    setActive((prev) => (prev ? { ...prev, mensajes: [...(prev.mensajes || []), optimistic] } : prev));
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    setText(""); setPendingImg(null);
    try {
      await api.post(`/conversations/${active.id}/mensaje`, payload);
      await refreshActive();
      loadList();
    } catch (e) { toast.error("Error al enviar"); }
    finally { setSending(false); }
  };

  const sendAsHuman = async () => {
    if (!text.trim()) return;
    const c = text; setText("");
    const optimistic = { id: `optimistic-${Date.now()}`, rol: "humano", tipo: "texto", contenido: c, timestamp: new Date().toISOString() };
    setActive((prev) => (prev ? { ...prev, mensajes: [...(prev.mensajes || []), optimistic] } : prev));
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    try { await api.post(`/conversations/${active.id}/humano`, { contenido: c }); await refreshActive(); loadList(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); setText(c); }
  };

  const openTemplateDialog = () => { setTplId(templates[0]?.id || ""); setTplValores({}); setTplDialogOpen(true); };
  const selectedTpl = templates.find((t) => t.id === tplId);
  const sendTemplate = async () => {
    if (!selectedTpl) return;
    const valores = (selectedTpl.variables || []).map((v) => tplValores[v] || "");
    try {
      await api.post(`/conversations/${active.id}/plantilla`, { template_id: tplId, valores });
      toast.success("Plantilla enviada"); setTplDialogOpen(false); refreshActive(); loadList();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const takeover = async () => { await api.post(`/conversations/${active.id}/takeover`); refreshActive(); loadList(); toast.success("Agente en pausa, respondés vos"); };
  const resume = async () => { await api.post(`/conversations/${active.id}/resume`); refreshActive(); toast.success("Agente reactivado"); };

  const asignar = async (userId) => {
    try {
      await api.put(`/conversations/${active.id}/asignar`, { user_id: userId || null });
      await refreshActive(); loadList();
      toast.success(userId ? "Conversación asignada" : "Conversación sin asignar");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const markExample = async (m) => {
    try { await api.post(`/messages/${m.id}/marcar-ejemplo`); toast.success("Guardado en la base de conocimiento"); refreshActive(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const addQuickReply = async () => {
    if (!newQuickReply.trim()) return;
    const { data } = await api.post("/respuestas-rapidas", { texto: newQuickReply });
    setQuickReplies((prev) => [data, ...prev]);
    setNewQuickReply("");
  };
  const deleteQuickReply = async (id) => {
    await api.delete(`/respuestas-rapidas/${id}`);
    setQuickReplies((prev) => prev.filter((q) => q.id !== id));
  };

  const openNotes = async () => {
    const r = await api.get(`/conversations/${active.id}/notas`);
    setNotes(r.data);
    setNotesOpen(true);
  };
  const addNote = async () => {
    if (!newNote.trim()) return;
    const { data } = await api.post(`/conversations/${active.id}/notas`, { texto: newNote });
    setNotes((prev) => [data, ...prev]);
    setNewNote("");
  };

  const filteredList = list?.filter((c) => !listQuery || contactAlias(c).toLowerCase().includes(listQuery.toLowerCase()));
  const visibleMessages = msgQuery
    ? active?.mensajes?.filter((m) => (m.contenido || "").toLowerCase().includes(msgQuery.toLowerCase()))
    : active?.mensajes;

  return (
    <div className="h-[calc(100vh-3.5rem)] md:h-screen flex">
      {/* List */}
      <div className={`w-full md:w-80 border-r border-border flex flex-col ${active ? "hidden md:flex" : "flex"}`}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-3">
            <h1 className="font-head text-xl font-semibold">Conversaciones</h1>
            <Button size="sm" onClick={newSimulator} className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 gap-1" data-testid="new-simulator">
              <Plus className="h-4 w-4" /> Prueba
            </Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {FILTROS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)}
                className={`text-xs rounded-full px-3 py-1 flex items-center gap-1 ${filter === f.key ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                data-testid={`filter-${f.key}`}>
                {f.label}
                {f.key === "esperando_humano" && list && (
                  <span className="bg-red-500 text-white rounded-full px-1.5">{list.filter((c) => c.estado === "derivada").length}</span>
                )}
              </button>
            ))}
          </div>
          <div className="relative mt-2">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={listQuery} onChange={(e) => setListQuery(e.target.value)} placeholder="Buscar por nombre…" className="rounded-full pl-9 h-8 text-sm" data-testid="search-conversations" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!list ? <Loader /> : filteredList.length === 0 ? (
            <div className="p-6"><EmptyState icon={MessageCircle} title="Sin conversaciones" subtitle="Creá una conversación de prueba para chatear con el agente." /></div>
          ) : filteredList.map((c) => (
            <button key={c.id} onClick={() => openConv(c)} data-testid={`conv-${c.id}`}
              className={`w-full text-left px-4 py-3 border-b border-border hover:bg-accent transition-colors ${active?.id === c.id ? "bg-accent" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm truncate" data-testid={`conv-alias-${c.id}`}>{contactAlias(c)}</span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className="text-[10px] text-muted-foreground" data-testid={`conv-time-${c.id}`}>{relTime(c.last_message_at)}</span>
                  {c.no_leidos > 0 && <span className="bg-primary text-primary-foreground text-[10px] rounded-full px-1.5">{c.no_leidos}</span>}
                </div>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5" data-testid={`conv-preview-${c.id}`}>
                {c.ultimo_mensaje_rol === "humano"
                  ? (c.ultimo_mensaje_autor_id && c.ultimo_mensaje_autor_id !== me?.id ? `${(c.ultimo_mensaje_autor_nombre || "Equipo").split(" ")[0]}: ` : "Vos: ")
                  : c.ultimo_mensaje_rol === "agente" ? "Agente: " : ""}{c.ultimo_mensaje_preview || "Sin mensajes todavía"}
              </p>
              <div className="flex items-center gap-2 mt-1">
                {(() => { const Icon = CHANNEL_ICON[c.canal] || MessageCircle; return <Icon className="h-3 w-3 text-muted-foreground" />; })()}
                <span className="text-[11px] text-muted-foreground">{CHANNEL_LABELS[c.canal] || c.canal}</span>
                <Badge className={`text-[10px] rounded-full border-0 ${stateBadge(c.estado)}`}>{CONV_STATE_LABELS[c.estado] || c.estado}</Badge>
                {!c.asignado_a && c.estado === "derivada" && (
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" title="Esperando humano, sin asignar" data-testid={`sin-asignar-dot-${c.id}`} />
                )}
                {c.asignado_a && (
                  <span className="ml-auto text-[10px] font-medium bg-accent rounded-full h-5 w-5 flex items-center justify-center shrink-0" title={c.asignado_a_nombre} data-testid={`asignado-badge-${c.id}`}>
                    {initials(c.asignado_a_nombre)}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat */}
      <div className={`flex-1 flex flex-col ${active ? "flex" : "hidden md:flex"}`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground p-8">
            <div className="text-center">
              <MessageCircle className="h-10 w-10 mx-auto mb-3 text-primary" strokeWidth={1.5} />
              <p>Elegí una conversación o creá una de prueba.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <button className="md:hidden" onClick={() => setActive(null)}><ArrowLeft className="h-5 w-5" /></button>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{contactAlias(active)}</p>
                <div className="flex items-center gap-2">
                  {(() => { const Icon = CHANNEL_ICON[active.canal] || MessageCircle; return <Icon className="h-3.5 w-3.5 text-muted-foreground" />; })()}
                  <span className="text-xs text-muted-foreground">{CHANNEL_LABELS[active.canal]}</span>
                  <Badge className={`text-[10px] rounded-full border-0 ${stateBadge(active.estado)}`}>{CONV_STATE_LABELS[active.estado]}</Badge>
                  {["whatsapp", "instagram"].includes(active.canal) && !active.ventana_abierta && (
                    <Badge className="text-[10px] rounded-full border-0 bg-amber-50 text-amber-600" data-testid="ventana-cerrada-badge">Ventana cerrada</Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <UserCheck className="h-4 w-4 text-muted-foreground shrink-0" />
                <Select value={active.asignado_a || "sin_asignar"} onValueChange={(v) => asignar(v === "sin_asignar" ? null : v)}>
                  <SelectTrigger className="rounded-full h-8 text-xs w-40" data-testid="asignar-select"><SelectValue placeholder="Asignar a" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin_asignar">Sin asignar</SelectItem>
                    {activeUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                {me && active.asignado_a !== me.id && (
                  <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={() => asignar(me.id)} data-testid="tomarla-yo-btn">Tomarla yo</Button>
                )}
              </div>
              {isHuman ? (
                <Button size="sm" variant="outline" onClick={resume} className="rounded-full gap-1" data-testid="resume-agent"><Play className="h-4 w-4" /> Reactivar agente</Button>
              ) : (
                <Button size="sm" onClick={takeover} className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 gap-1" data-testid="takeover"><Pause className="h-4 w-4" /> Responder yo</Button>
              )}
              <Button size="sm" variant="ghost" onClick={openNotes} className="rounded-full gap-1" data-testid="open-notes"><StickyNote className="h-4 w-4" /></Button>
            </div>

            {active.motivo_derivacion && active.estado === "derivada" && (
              <div className="bg-red-50 text-red-600 text-xs px-4 py-2">Derivada: {active.motivo_derivacion}</div>
            )}

            <div className="px-4 py-2 border-b border-border">
              <div className="relative">
                <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={msgQuery} onChange={(e) => setMsgQuery(e.target.value)} placeholder="Buscar en esta conversación…" className="rounded-full pl-8 h-8 text-xs" data-testid="search-messages" />
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-background">
              {visibleMessages?.map((m) => <Bubble key={m.id} m={m} onMarkExample={markExample} />)}
              {sending && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Sparkles className="h-3 w-3 animate-pulse text-primary" /> El agente está escribiendo…</div>}
            </div>

            <div className="border-t border-border p-3">
              {pendingImg && (
                <div className="mb-2 flex items-center gap-2">
                  <img src={fileUrl(pendingImg)} alt="" className="h-12 w-12 rounded-lg object-cover" />
                  <button onClick={() => setPendingImg(null)} className="text-xs text-red-500">Quitar</button>
                </div>
              )}
              {isHuman ? (
                active.canal === "whatsapp" && !active.ventana_abierta ? (
                  <div className="flex items-center justify-between gap-2 rounded-2xl bg-amber-50 px-3 py-2">
                    <p className="text-xs text-amber-700">Ventana de 24h cerrada. Usá una plantilla aprobada.</p>
                    <Button size="sm" onClick={openTemplateDialog} className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shrink-0" data-testid="open-template-dialog">Usar plantilla</Button>
                  </div>
                ) : active.canal === "instagram" && !active.ventana_abierta && !active.ventana_extendida_abierta ? (
                  <p className="text-center text-xs text-red-500 py-2">Fuera de la ventana de mensajería permitida por Instagram (máx. 7 días desde su último mensaje).</p>
                ) : (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-xs text-amber-700 shrink-0"><UserCog className="h-4 w-4" /></div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="shrink-0 text-muted-foreground hover:text-primary" data-testid="quick-replies-trigger"><Zap className="h-5 w-5" /></button>
                    </PopoverTrigger>
                    <PopoverContent className="w-72 p-2" align="start">
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Respuestas rápidas</p>
                      <div className="space-y-1 max-h-48 overflow-y-auto">
                        {quickReplies.map((q) => (
                          <div key={q.id} className="flex items-center gap-1 group">
                            <button onClick={() => setText(q.texto)} className="flex-1 text-left text-sm rounded-lg px-2 py-1.5 hover:bg-accent truncate" data-testid={`quick-reply-${q.id}`}>{q.texto}</button>
                            <button onClick={() => deleteQuickReply(q.id)} className="opacity-0 group-hover:opacity-100 text-red-400" data-testid={`delete-quick-reply-${q.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </div>
                      <div className="flex items-center gap-1 mt-2 border-t border-border pt-2">
                        <Input value={newQuickReply} onChange={(e) => setNewQuickReply(e.target.value)} placeholder="Nueva respuesta…" className="rounded-full h-8 text-xs" data-testid="new-quick-reply-input" />
                        <Button size="sm" onClick={addQuickReply} className="rounded-full h-8 shrink-0" data-testid="add-quick-reply">+</Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {active.canal === "instagram" && !active.ventana_abierta && (
                    <p className="text-[10px] text-amber-600 shrink-0 max-w-[90px]">Se enviará como agente humano</p>
                  )}
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendAsHuman()}
                    placeholder="Respondé como equipo…" className="rounded-full" data-testid="human-input" />
                  <Button onClick={sendAsHuman} className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="send-human"><Send className="h-4 w-4" /></Button>
                </div>
                )
              ) : canSendClient ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground shrink-0"><User className="h-4 w-4" /></div>
                  <label className="shrink-0 cursor-pointer text-muted-foreground hover:text-primary">
                    <input type="file" accept="image/*" className="hidden" onChange={pickImage} data-testid="sim-image" />
                    <ImagePlus className="h-5 w-5" />
                  </label>
                  <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendAsClient()}
                    placeholder="Escribí como cliente…" className="rounded-full" data-testid="client-input" disabled={sending} />
                  <Button onClick={sendAsClient} disabled={sending} className="rounded-full bg-primary hover:bg-primary/90" data-testid="send-client"><Send className="h-4 w-4" /></Button>
                </div>
              ) : (
                <p className="text-center text-xs text-muted-foreground py-2 flex items-center justify-center gap-1"><Bot className="h-4 w-4 text-primary" /> El agente IA está atendiendo. Tocá "Responder yo" para tomar la conversación.</p>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-sm max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><StickyNote className="h-5 w-5 text-primary" /> Notas internas</DialogTitle>
            <DialogDescription>No son visibles para el cliente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Escribí una nota…" className="rounded-xl" data-testid="new-note-input" />
            <Button onClick={addNote} className="rounded-full bg-primary hover:bg-primary/90 w-full" data-testid="add-note-button">Agregar nota</Button>
          </div>
          <div className="space-y-2 mt-2">
            {notes.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Sin notas todavía.</p> : notes.map((n) => (
              <div key={n.id} className="rounded-xl bg-accent/50 px-3 py-2" data-testid={`note-${n.id}`}>
                <p className="text-sm">{n.texto}</p>
                <p className="text-[10px] text-muted-foreground mt-1">{n.autor} · {dt(n.created_at)}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSignature className="h-5 w-5 text-primary" /> Enviar plantilla de WhatsApp</DialogTitle>
            <DialogDescription>Elegí una plantilla aprobada y completá sus variables para enviarla fuera de la ventana de 24h.</DialogDescription>
          </DialogHeader>
          {templates.length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay plantillas registradas. Agregá una en Canales → Plantillas.</p>
          ) : (
            <div className="space-y-3">
              <Select value={tplId} onValueChange={(v) => { setTplId(v); setTplValores({}); }}>
                <SelectTrigger className="rounded-xl" data-testid="select-template"><SelectValue /></SelectTrigger>
                <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.nombre_meta}</SelectItem>)}</SelectContent>
              </Select>
              {selectedTpl?.cuerpo_ejemplo && <p className="text-xs text-muted-foreground">{selectedTpl.cuerpo_ejemplo}</p>}
              {(selectedTpl?.variables || []).map((v) => (
                <Input key={v} placeholder={v} value={tplValores[v] || ""} onChange={(e) => setTplValores({ ...tplValores, [v]: e.target.value })} className="rounded-xl" data-testid={`tpl-valor-${v}`} />
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setTplDialogOpen(false)}>Cancelar</Button>
            <Button onClick={sendTemplate} disabled={!tplId} className="rounded-full bg-primary hover:bg-primary/90" data-testid="send-template">Enviar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
